import { useEffect, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  Center,
  Instances,
  Instance,
  Bounds
} from "@react-three/drei";
import * as Tone from 'tone'
import * as THREE from 'three'

let lerp = THREE.MathUtils.lerp
let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]

let twin = randI(100) > 70
let bg = 'white'
let colorized = randI(100)> 60
let colors = [
   colorized ? [
    '#14a160',
    'hotpink',
    '#2969de',  
    '#ff5937',  
  ]: [
    '#000',
  ],
  [
    '#000',
  ]
]
let light = rand([true, false])
const feats = window.$fxhashFeatures = {
  width: 1+randI(16),
  rotation: rand(twin ? [
    0.25,
    1.25, 
    0.25,
    1.25, 
    0.25,
    1.25, 
    0.25,
    1.25, 
    0.25,
    1.25, 
    0.25,
    1.25, 
    0.5, 
    1,
    1.5
  ] : [
    0, 
    0, 
    0, 
    0.25, 
    0.5, 
    0.5, 
    0.5, 
    0.75, 
    1, 
    1, 
    1, 
    1.25, 
    1.5, 
    1.5,
    1.5,
  ]),
  light,
  waves: rand([0.5, 1, 2, 3, 4]),
  zoom: 2 + randI(8) *(twin ? 0.25 : 1),
  sequenceLength: rand([2,4]),
  twin,
  color: light && colorized
}

let padLength = 16
let pads = new Array(padLength).fill().map(() => {
  let am = new Tone.AMSynth({
    "harmonicity": 1,
    "oscillator": {
      "type": "amsine2",
      "modulationType": "sine",
      "harmonicity": rand([1.01,1.03, 1.01, 1.015, 1.02, 1.03, 1.04])
    },
    "envelope": {
      "attack": '2m',
      "decay": '1m',
      "sustain": 0.5,
      "release": '1m'
    },
    "modulation": {
      "volume": rand([5, 10, 15, 18]),
      "type": "amsine2",
      "modulationType": "sine",
      "harmonicity": rand([1, 2,4,8])
    },
    "modulationEnvelope": {
      "attack": '1m',
      "decay": 0.2,
      "sustain": 0.5,
      "release": '1m'
    }
  })
  let delay = new Tone.PingPongDelay('8n', 0.9).toDestination()
  am.connect(new Tone.Gain(0.3).connect(delay))
  am.connect(new Tone.Gain(1.2).connect(new Tone.Panner((-10 + randI(20)) * 0.1).toDestination()))
  return am
})
let chimes = new Array(padLength).fill().map(() => {
  let am = new Tone.FMSynth({
    "harmonicity": 8,
    "modulationIndex": 2,
    "portamento": 2,
    "oscillator": {
      "type": "sine"
    },
    "envelope": {
      "attack": 0.003,
      "decay": 2,
      "sustain": 0.1,
      "release": 2
    },
    "modulation": {
      "type": "square"
    },
    "modulationEnvelope": {
      "attack": 0.002,
      "decay": 0.2,
      "sustain": 0,
      "release": 0.2
    }
  })
  let delay = new Tone.PingPongDelay('4t', 0.825)
  am.connect(new Tone.Gain(0.2).connect(delay.toDestination()))
  am.connect(new Tone.Gain(0.7).connect(new Tone.Panner((-10 + randI(20)) * 0.1).toDestination()))
  return am
})

Tone.context.lookAhead = 0.1
Tone.Transport.bpm.value = 80

let patternLength = 128
let rotations = new Array(patternLength).fill(0)
let targetRotations = new Array(patternLength).fill().map((_, i) => i * 0.04)
let scales = new Array(patternLength).fill(1)
let targetScales = new Array(patternLength).fill(1)
let notes = new Array(16).fill().map(() => rand(['G1', 'A1', 'C2', 'D2', 'E2']))

let loopCount = 0
let padCount = 0
let getVel = () => (3 + randI(5)) * 0.02
let loop = new Tone.Loop(() => {
  let add = Math.PI * feats.waves
  targetRotations[loopCount % patternLength] += add
  if (loopCount % (patternLength / 2) === 0) {
    pads[padCount % padLength].triggerAttackRelease(notes[padCount % feats.sequenceLength], '4m', '+0.001', getVel() * 4.5)
    pads[(padCount + 1) % padLength].triggerAttackRelease(notes[padCount % feats.sequenceLength], '4m', '+0.001', getVel() * 4.5)
    pads[(padCount + 2) % padLength].triggerAttackRelease(notes[padCount % feats.sequenceLength], '4m', '+0.001', getVel() * 4.5)
    padCount++;
  }
  loopCount++
}, '32n')
let started = false
export default function App() {
  const click = () => {
    if (!started) {
      Tone.start();
      Tone.Transport.start()
      loop.start()
      started = true
    } else {
      targetRotations = new Array(patternLength).fill(0)
      rotations = rotations.map((r, ri) => r % Math.PI)
      loopCount = 0
      padCount = 0
      loop.stop()
      started = false
    }
  }
  useEffect(() => {
    if (!feats.light) {
      document.getElementById('overlay').style.display = 'unset'
    }
  })
  return (
    <div id="main" onClick={() => click()} >
      <div id='overlay' />
      <Canvas flat={true} linear={true} orthographic camera={{ position: [0, 0, 100], zoom: 80, near: 0.01 }}>
        <All />
      </Canvas>
    </div >
  );
}
let geo = new THREE.BoxBufferGeometry(0.3, feats.width * 0.5, 0.3).scale(2, 2, 2)
let playable = true
let chimeNotes = []
let octs = 3
new Array(octs).fill().map((__, o) => ['C', 'D', 'E', 'F', 'G', 'A', 'B'].forEach((n, ni) => {
  chimeNotes.push(n + (o + 1))
}))
const Box = ({ i }) => {
  const box = useRef()
  useFrame(() => {
    rotations[i] = lerp(rotations[i], targetRotations[i], 0.0025 * (feats.twin ? 1 : 2))
    scales[i] = lerp(scales[i], targetScales[i], 0.04)
    box.current.rotation.y = rotations[i]
    box.current.scale.z = scales[i]
    box.current.scale.x = scales[i]
  })
  return (
    <Instance ref={box} onPointerEnter={() => {
      if (playable) {
        targetScales[i] = feats.zoom
        chimes[chimeCount % padLength].triggerAttackRelease(notes[padCount % feats.sequenceLength][0] + '7', 0.01, '+0.001', getVel() * 0.25)
        chimeCount++;
        playable = false
        setTimeout(() => {
          playable = true
        }, 50)
      }
    }} />
  );
};
console.table(feats)
let chimeCount = 0
const All = ({ i }) => {
  useFrame(() => {
    targetScales.forEach((_, i) => {
      targetScales[i] = lerp(targetScales[i], 1, 0.2)
    })
  })
  return (
    <>
      <Bounds fit margin={1.2} damping={0}>
        <Center>
          <group rotation={[0, 0, Math.PI * feats.rotation]}>
            <Instances
              material={new THREE.MeshStandardMaterial({ color: bg, emissive: rand(colors[feats.light ? 0 : 1]), depthTest: true })}
              geometry={geo}
            >
              {new Array(patternLength).fill().map((_, i) =>
                <group position={[0.125 * i, 0, 0]}>
                  <Box i={i} />
                </group>
              )}
            </Instances>
          </group>
        </Center>
        {feats.twin && <Center>
          <group rotation={[0, 0, (Math.PI * feats.rotation) + (Math.PI * 0.5)]}>
            <Instances
              material={new THREE.MeshStandardMaterial({ color: bg, emissive: rand(colors[feats.light ? 0 : 1]), depthTest: true })}
              geometry={geo}
            >
              {new Array(patternLength).fill().map((_, i) =>
                <group position={[0.125 * i, 0, 0]} key={i}>
                  <Box i={i} />
                </group>
              )}
            </Instances>
          </group>
        </Center>}
      </Bounds>
      <directionalLight intensity={1.3} position={[0, 0, 1]} />
      <color attach={'background'} args={[bg]} />
    </>
  );
};

console.log('%c 🌊🌊 @ramioso 2022-09-13 🌊🌊', 'color: orange');

